// Guest identity (signed token) for Word Dash.
//
// The token is read ONCE from the ?t= query param on first load and persisted
// to localStorage so it survives in-session navigation. It is sent on every
// authenticated API call (progress + score submission). The display name shown
// on the leaderboard is read from the token payload, so there is no free-text
// name entry when a token is present.
//
// The token is only a bearer of identity the SERVER verifies; the client does
// not and cannot validate the signature (it has no secret). It just carries it.

const TOKEN_KEY = 'wdGuestToken'

// Decode the base64url payload of a token WITHOUT verifying (display only).
function decodePayload(token: string): { id?: string; name?: string } | null {
  try {
    const payloadB64 = token.split('.')[0]
    const b64 = payloadB64.replace(/-/g, '+').replace(/_/g, '/')
    const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4))
    const bin = atob(b64 + pad)
    const bytes = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
    const obj = JSON.parse(new TextDecoder().decode(bytes))
    return obj && typeof obj === 'object' ? obj : null
  } catch {
    return null
  }
}

// A valid token is `<base64url>.<base64url>`. Strip any character outside that
// alphabet so a stray paste artifact (a trailing backtick from a markdown code
// fence, a quote, whitespace, a trailing slash) can't silently corrupt an
// otherwise-good link and fail the server signature check. We only trim the
// edges and remove illegal chars; we never reorder or pad.
const sanitizeToken = (raw: string): string =>
  raw.trim().replace(/[^A-Za-z0-9\-_.]/g, '')

// Read ?t= from the URL (if present), store it, and strip it from the address
// bar so it is not left lying in history/screenshots. Call once on app load.
export const captureTokenFromUrl = (): void => {
  try {
    const url = new URL(window.location.href)
    const t = url.searchParams.get('t')
    if (t) {
      const clean = sanitizeToken(t)
      if (clean) localStorage.setItem(TOKEN_KEY, clean)
      url.searchParams.delete('t')
      window.history.replaceState({}, '', url.toString())
    }
  } catch {
    // no-op
  }
}

// One-time self-heal for tokens already stored (before sanitization existed)
// with a stray character. If the stored value differs from its sanitized form,
// rewrite it. Harmless no-op for clean tokens. Call once on load.
export const healStoredToken = (): void => {
  try {
    const raw = localStorage.getItem(TOKEN_KEY)
    if (!raw) return
    const clean = sanitizeToken(raw)
    if (clean && clean !== raw) localStorage.setItem(TOKEN_KEY, clean)
  } catch {
    // no-op
  }
}

export const getToken = (): string => localStorage.getItem(TOKEN_KEY) || ''

export const hasToken = (): boolean => getToken().length > 0

// Display name baked into the token, if any.
export const getTokenName = (): string => {
  const t = getToken()
  if (!t) return ''
  const p = decodePayload(t)
  return (p?.name || '').toString()
}

// Guest id baked into the token, if any. Used to scope locally-cached state
// (e.g. the board) per identity so switching links in the same browser doesn't
// bleed one guest's cached board into another's. Empty string when no token
// (anonymous/dev play), which keeps the legacy un-scoped key.
export const getGuestId = (): string => {
  const t = getToken()
  if (!t) return ''
  const p = decodePayload(t)
  return (p?.id || '').toString()
}

// Shallow "does this look like one of our tokens" check for the in-app gate.
// This is NOT security — the client has no secret and cannot verify the HMAC
// signature. The real check runs server-side on every write (401 otherwise).
// It only decides whether to show the game vs. an "invite required" screen, so
// a stranger with no link sees the gate instead of the board.
export const looksLikeValidToken = (): boolean => {
  const t = getToken()
  if (!t) return false
  const dot = t.indexOf('.')
  if (dot <= 0 || dot === t.length - 1) return false
  const p = decodePayload(t)
  return !!(
    p &&
    typeof p.id === 'string' &&
    typeof p.name === 'string' &&
    p.id &&
    p.name
  )
}

// Authorization header for authenticated requests (empty object if no token).
export const authHeaders = (): Record<string, string> => {
  const t = getToken()
  return t ? { authorization: `Bearer ${t}` } : {}
}

// Result of probing the stored token against the server's real HMAC check.
//   'valid'   -> the server verified the signature (200). The token works.
//   'invalid' -> the server rejected it (401). Stale/forged link; wipe + gate.
//   'no-token'-> nothing stored to check.
//   'unknown' -> network/transport failure; can't decide (don't gate on this).
export type TokenCheck = 'valid' | 'invalid' | 'no-token' | 'unknown'

const PROGRESS_API = process.env.REACT_APP_PROGRESS_API || '/api/progress'

// Validate the stored token against the server. Uses GET /api/progress, which
// is side-effect free: it returns 200 (progress may be null) for a token whose
// signature verifies, and 401 for one that does not. This is the ONLY way the
// client can know a token is genuinely good — it holds no secret and cannot
// verify the HMAC locally. Call once on load so a stale/invalid link fails
// early (an invite screen) instead of letting the guest play into a submit
// wall. A network error returns 'unknown' so a flaky connection never falsely
// locks a guest out.
export const validateToken = async (): Promise<TokenCheck> => {
  const t = getToken()
  if (!t) return 'no-token'
  try {
    const res = await fetch(PROGRESS_API, {
      method: 'GET',
      headers: { accept: 'application/json', authorization: `Bearer ${t}` },
    })
    if (res.status === 401) return 'invalid'
    if (res.ok) return 'valid'
    // Any other status (500 etc.) is not a verdict on the token itself.
    return 'unknown'
  } catch {
    return 'unknown'
  }
}

// Remove the stored token (and let the caller reload into the invite gate).
export const clearToken = (): void => {
  try {
    localStorage.removeItem(TOKEN_KEY)
  } catch {
    // no-op
  }
}
